import React from 'react';

import { faMap } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DateTime } from 'luxon';
import { capitalize } from 'lodash';

import { SkeletonLoader } from '@clutter/clean';

import { Spacer } from '@shared/components/helpers';

import { TermCommitments } from '@admin/components/account/term_commitments';
import { CopyURL } from '@admin/components/helpers/copy_url';
import { Label } from '@admin/components/helpers/label';
import { titleize } from '@admin/components/helpers/titleize';
import { OrderKindDescription } from '@admin/components/order_summary/order_kind_description';
import { FormattedServiceType } from '@admin/components/orders/formatted_service_type';
import { LaborPolicy } from '@admin/components/orders/labor_policy';
import { MultiDayContext } from '@admin/components/orders/multi_day_context';
import { OrderTags } from '@admin/components/orders/order_tags';
import { BookedByToggle } from '@admin/components/orders/show/booked_by_toggle';
import { CouponToggle } from '@admin/components/orders/show/coupon_toggle';
import { OrderPricingVisibility } from '@admin/components/orders/show/order_pricing_visibility';
import { ReclassifyReturnSubtypeButton } from '@admin/components/orders/show/reclassify_return_subtype_button';
import { State } from '@admin/components/orders/state';
import { WaitlistRequests } from '@admin/components/orders/waitlist_requests';
import { Formatter } from '@admin/components/phone/formatter';
import { Summary } from '@admin/components/subscriptions/summary';
import { dispatchURL, mapURL, orderURL } from '@admin/config/routes';
import { client } from '@admin/libraries/apollo';
import { Customer, Order, OrderStateEnum, OrderSubtypeEnum, OrderTypeEnum, useOrderShowQuery } from '@admin/schema';

import { LongDistanceMoveSummary } from '../long_distance_move_summary';

function formatDate(date: string, format = DateTime.DATETIME_MED) {
  return DateTime.fromISO(date).toLocaleString(format);
}

const CUFT_SOURCE_LABELS: Record<string, string> = {
  plan_size: 'Plan Size',
  agent_estimate: 'Agent Estimate',
  customer_inventory: 'Customer Inventory',
};

type CustomerFragment = Pick<Customer, 'id' | 'phone' | 'name'>;

const CustomerNames: React.FC<{ customers: CustomerFragment[]; primaryCustomer: CustomerFragment }> = ({
  customers,
  primaryCustomer,
}) => (
  <>
    {[primaryCustomer.name]
      .concat(
        customers
          .filter((c) => c.id !== primaryCustomer.id)
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((c) => c.name),
      )
      .join(', ')}
  </>
);

export const OrderSummaryFields: React.FC<{
  orderID: string;
}> = ({ orderID }) => {
  const { data: { order } = {}, error } = useOrderShowQuery({
    client,
    variables: { id: orderID! },
    skip: !orderID,
    fetchPolicy: 'cache-and-network',
  });

  if (error) throw error;
  if (!order) return <SkeletonLoader height="320px" />;

  const account = order.account;

  const scheduledDateTime = DateTime.fromISO(order.scheduled).setZone(order.region.tz);
  const date = scheduledDateTime.toFormat('MMM d, y');

  return (
    <>
      <p className="position-relative">
        <strong>ID:</strong> {order.id}
        <CopyURL />
        {order.dispatch?.id && (
          <a
            href={mapURL({ orderID: order.id, accountID: account.id })}
            className="position-absolute"
            style={{ marginLeft: '42px;' }}
            target="_blank"
          >
            <FontAwesomeIcon icon={faMap} />
          </a>
        )}
      </p>
      <p>
        <strong>Kind:</strong> <OrderKindDescription order={order} /> <OrderTags id={order.id} />
        <ReclassifyReturnSubtypeButton
          orderID={order.id}
          status={order.status}
          subtype={order.subtype}
          type={order.type}
        />
        {account.partnerManaged && (
          <>
            {' '}
            <span title="Account is managed by an external partner">
              <Label kind="info">Partner-managed</Label>
            </span>
          </>
        )}
      </p>
      <p>
        <strong>Service Type:</strong> <FormattedServiceType order={order} />
      </p>
      <p>
        <strong>Labor:</strong>
        {order.id && (
          <>
            {' '}
            <LaborPolicy orderId={order.id} />
          </>
        )}
      </p>
      {order.cancellationPolicy && (
        <p>
          <strong>Cancellation Policy:</strong>
          <ul>
            {order.cancellationPolicy.summaries.map((summary, key) => (
              <li key={key}>{summary}</li>
            ))}
          </ul>
        </p>
      )}
      {!order.parent && !order.predecessor?.id && (
        <p>
          <strong>Coupon:</strong> <CouponToggle order={order} />
        </p>
      )}
      {account.currentStorageSubscription && (
        <p>
          <strong>Current Plan:</strong>
          <Summary subscriptions={[account.currentStorageSubscription]} showCost />
        </p>
      )}
      <TermCommitments accountID={account.id} />
      <OrderPricingVisibility
        currentStorageSubscription={account.currentStorageSubscription}
        pickupServicePackageOptions={order.pickupServicePackageOptions}
        storageLadderPricings={account.storageLadderPricings}
        tags={order.tags}
      />
      <p>
        <strong>Estimated Cuft:</strong>{' '}
        {order.estimatedCuft != null && Number.isFinite(order.estimatedCuft) ? order.estimatedCuft.toFixed(2) : '—'}
        {order.type === OrderTypeEnum.Pickup && order.estimatedCuftSource && (
          <>
            {' '}
            via {CUFT_SOURCE_LABELS[order.estimatedCuftSource] || order.estimatedCuftSource} (Customer Inventory{' '}
            {order.customerInventoryComplete ? 'Complete' : 'Incomplete'})
          </>
        )}
      </p>
      <p>
        <strong>{order.dropOffAppointment ? 'Internal Transfer: ' : 'Scheduled: '}</strong>
        {order.state === OrderStateEnum.Pending ? (
          'N/A'
        ) : (
          <>
            {date}, {order.formattedArrivalWindow}
          </>
        )}
      </p>
      {order.dropOffAppointment && (
        <p>
          <strong>Customer Arrival:</strong>
          <span>{DateTime.fromISO(order.dropOffAppointment.expectedAt).toLocaleString(DateTime.DATETIME_MED)}</span>
        </p>
      )}
      <p>
        <WaitlistRequests orderID={order.id} />
      </p>
      <p>
        <strong>State:</strong> <State state={order.state} waitlisted={order.waitlistRequests.length > 0} />
        {order.state === OrderStateEnum.Canceled && order.cancelReason && ' - ' + order.cancelReason.kind}
      </p>
      <p>
        <strong>Movers:</strong> {order.movers}
        {order.dispatch && (
          <>
            {` - `}
            <a href={dispatchURL({ id: order.dispatch.id })}>
              {order.dispatch.assignments
                .map((assignment) => `${assignment.user.name} (${assignment.role})`)
                .join(', ')}
            </a>
          </>
        )}
      </p>
      {order.flaggedAt && (
        <p>
          <strong>Flagged:</strong>
          {formatDate(order.flaggedAt)}
        </p>
      )}
      {order.review && (
        <p>
          <strong>Rating:</strong> <span className="label label-default">{capitalize(order.review.rating)}</span>{' '}
          <span>
            on {formatDate(order.review.updatedAt || order.review.createdAt)}{' '}
            {order.review.comments && ` - "${order.review.comments}"`}
          </span>
        </p>
      )}
      {order.eta && (
        <p>
          <strong>ETA:</strong> <span>{formatDate(order.eta)}</span>
        </p>
      )}
      {order.shipmentValuationDeclaration && (
        <p>
          <strong>Shipment Valuation:</strong> <span>{order.shipmentValuationDeclaration.description}</span>
        </p>
      )}
      {order.movingOperation && (
        <p>
          <strong>Travel Time Multiplier:</strong>{' '}
          <span>{order.movingOperation.quote?.movingConfiguration?.travelRateMultiplier}</span>
        </p>
      )}
      {order.parent && order.type === OrderTypeEnum.Restock && (
        <p>
          <strong>Restock of Order: </strong>
          <a href={orderURL({ id: order.parent.id, account: { id: account.id } })} className="underlined_link">
            {order.parent.number}
          </a>
        </p>
      )}
      {order.parent && order.type !== OrderTypeEnum.Restock && (
        <p>
          <strong>Attached to Parent Order: </strong>
          <a href={orderURL({ id: order.parent.id, account: { id: account.id } })} className="underlined_link">
            #{order.parent.number}
          </a>
        </p>
      )}
      {order.children.map((child) => (
        <p key={child.id}>
          <strong>Attached to Child Order: </strong>
          <a href={orderURL({ id: child.id, account: { id: account.id } })} className="underlined_link">
            #{child.number}
          </a>
        </p>
      ))}
      <MultiDayContext
        orderID={order.id}
        goToOrder={({ id, accountID }) => (window.location.pathname = orderURL({ id, account: { id: accountID } }))}
      />
      <p>
        <strong>Customer(s):</strong> <CustomerNames customers={account.customers} primaryCustomer={account.customer} />
      </p>
      <p>
        <strong>Phone:</strong>
        {account.customer.phone ? <Formatter number={account.customer.phone} /> : 'N/A'}
      </p>
      {order.contact && (
        <p>
          <strong>Day-of Contact:</strong> {order.contact.name}{' '}
          {order.contact.phone && (
            <>
              (Phone: <Formatter number={order.contact.phone} />)
            </>
          )}
        </p>
      )}
      <BookedByToggle order={order} />

      <Spacer height="8px" />
      {order.longDistanceMove && <LongDistanceMoveSummary move={order.longDistanceMove} />}
    </>
  );
};
